import React, { useState } from "react";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import useKeysPressed from "../hooks/useKeysPressed";
import useListenerPresence from "../hooks/useListenerPresence";
import useShowAboutFirstTime from "../hooks/useShowAboutFirstTime";
import useStationFromUrl from "../hooks/useStationFromUrl";
import useTweetStation from "../hooks/useTweetStation";
import {
  aboutShownState,
  disableShortcutState,
  lowEnergyModeState,
  playerShownState,
  pomodoroShownState,
} from "../recoilState";
import sounds from "../sounds";
import strings from "../strings";
import detectFullscreenAvailable from "../utils/detectFullscreenAvailable";
import About from "./About";
import GifBackground, { useChangeGif, useShowStatic } from "./GifBackground";
import Player from "./Player";
import PlayPauseArea from "./PlayPauseArea";
import PomodoroTimer from "./PomodoroTimer";
import PressToStart from "./PressToStart";
import RoomActions from "./RoomActions";
import VisitorsCounter from "./VisitorsCounter";

function Room() {
  const [playerShown, setPlayerShown] = useRecoilState(playerShownState);
  const [lowEnergyMode, setLowEnergyMode] = useRecoilState(lowEnergyModeState);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const listenerCount = useListenerPresence(isListening && isPlaying);
  const setPomodoroShown = useSetRecoilState(pomodoroShownState);
  const setAboutShown = useSetRecoilState(aboutShownState);
  const isChecked = useRecoilValue(disableShortcutState);

  const activeKeyPresses = !isChecked;

  const showStatic = useShowStatic();
  const changeGif = useChangeGif();
  const tweetStation = useTweetStation();
  useShowAboutFirstTime(isPlaying);
  useStationFromUrl();

  const locale = strings.getLanguage();
  const isJapanese = locale === "ja";

  const fs = useFullScreenHandle();
  const fsAvailable = detectFullscreenAvailable();

  function handleStationChanged(playSound = true) {
    showStatic(300);
    if (playSound) sounds.static.play();
    changeGif();
  }

  function handleStart() {
    if (!playerShown) {
      setPlayerShown(true);
      setIsPlaying(true);
      // handleStationChanged();
    }
  }

  useKeysPressed(
    [
      [
        "Escape",
        () => {
          setAboutShown(false);
          setPomodoroShown(false);
        },
      ],
      ["t", tweetStation],
      ["l", () => setLowEnergyMode(!lowEnergyMode)],
      ["g", changeGif],
      ["any", handleStart],
    ],
    activeKeyPresses
  );

  return (
    <FullScreen handle={fs}>
      <div
        id="container"
        className={[
          lowEnergyMode ? "low-energy" : "",
          !playerShown ? "pointer" : "",
          isJapanese ? "ja" : "",
        ]
          .join(" ")
          .trim()}
        onClick={handleStart}
      >
        {playerShown && (
          <PlayPauseArea isPlaying={isPlaying} setIsPlaying={setIsPlaying} />
        )}
        <GifBackground />
        <div id="crt-lines" />
        <div id="darken" />
        <div id="vignette" />
        <div id="top-ui">
          <div>
            <VisitorsCounter count={listenerCount} />
          </div>

          <div className="vertical">
            {playerShown && (
              <RoomActions fullscreen={fs} fullscreenAvailable={fsAvailable} />
            )}

            <About checked={isChecked} />
            <PomodoroTimer />
          </div>
        </div>

        {!playerShown && <PressToStart />}
        <Player
          isPlaying={isPlaying}
          setIsPlaying={setIsPlaying}
          onListeningChange={setIsListening}
          onStationChanged={handleStationChanged}
        />
      </div>
    </FullScreen>
  );
}

export function detectTouch() {
  return "ontouchend" in document;
}

export default Room;
